import React from 'react';

import SecurityIcon from '@mui/icons-material/Security';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import BusinessIcon from '@mui/icons-material/Business';
import PeopleIcon from '@mui/icons-material/People';
import EventIcon from '@mui/icons-material/Event';
import LocalLibraryIcon from '@mui/icons-material/LocalLibrary';
import EmailIcon from '@mui/icons-material/Email';
import SportsIcon from '@mui/icons-material/Sports';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SchoolIcon from '@mui/icons-material/School';
import StoreIcon from '@mui/icons-material/Store';
import ChurchIcon from '@mui/icons-material/Church';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import ShieldIcon from '@mui/icons-material/Shield';
import ConstructionIcon from '@mui/icons-material/Construction';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import WorkIcon from '@mui/icons-material/Work';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CampaignIcon from '@mui/icons-material/Campaign';
import EngineeringIcon from '@mui/icons-material/Engineering';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import FenceIcon from '@mui/icons-material/Fence';
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import TrafficIcon from '@mui/icons-material/Traffic';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import ApprovalIcon from '@mui/icons-material/Approval';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import MuseumIcon from '@mui/icons-material/Museum';
import PoolIcon from '@mui/icons-material/Pool';
import HikingIcon from '@mui/icons-material/Hiking';

// Map of main categories to icons
const MAIN_CATEGORY_ICONS = {
    'viranomaiset': <VerifiedUserIcon />,
    'politiikka': <AccountBalanceIcon />,
    'henkilöt': <PeopleIcon />,
    'organisaatiot': <BusinessIcon />,
    'tiedotteet': <CampaignIcon />,
    'tapahtumat': <EventIcon />,
    'laitokset': <BusinessIcon />,
    'toimituksen sähköpostit': <EmailIcon />,
};

// Map of specific subcategories to icons
const SUBCATEGORY_ICONS = {
    'poliisi': <LocalPoliceIcon />,
    'pelastus': <LocalFireDepartmentIcon />,
    'oikeuslaitokset': <GavelIcon />,
    'puolustusvoimat': <MilitaryTechIcon />,
    'rajavartiolaitos': <FenceIcon />,
    'päätökset': <AssuredWorkloadIcon />,
    'urheilu': <SportsSoccerIcon />,
    'terveydenhuolto': <LocalHospitalIcon />,
    'oppilaitokset': <SchoolIcon />,
    'kauppakeskukset': <StoreIcon />,
    'uskonto': <ChurchIcon />,
    'kaupungit': <LocationCityIcon />,
    'liikenne': <TrafficIcon />,
    'yhdyskuntapalvelut': <EngineeringIcon />,
    'yritykset': <BusinessCenterIcon />,
    'julkishallinto': <AssuredWorkloadIcon />,
    'media': <LibraryBooksIcon />,
    'poliitikot': <AccountBalanceIcon />,
    'kuulutukset': <ApprovalIcon />,
    'kulttuuri': <TheaterComedyIcon />,
    'kirjastot': <AutoStoriesIcon />,
    'museot': <MuseumIcon />,
    'uimahallit': <PoolIcon />,
    'virkistysalueet': <HikingIcon />,
    'tulli': <FenceIcon />,
    'viranhaltijapäätökset': <AssuredWorkloadIcon />,
};

// Map of category to main category (for fallback icon lookup)
const CATEGORY_TO_MAIN_CATEGORY = {
    // viranomaiset
    'kuulutukset': 'viranomaiset',
    'oikeuslaitokset': 'viranomaiset',
    'pelastus': 'viranomaiset',
    'poliisi': 'viranomaiset',
    'puolustusvoimat': 'viranomaiset',
    'rajavartiolaitos': 'viranomaiset',
    'tulli': 'viranomaiset',
    'viranhaltijapäätökset': 'viranomaiset',
    
    // politiikka
    'päätökset': 'politiikka',
    
    // henkilöt
    'asiantuntijat': 'henkilöt',
    'julkisuuden henkilöt': 'henkilöt',
    'poliitikot': 'henkilöt',
    'urheilijat': 'henkilöt',
    'virkamiehet': 'henkilöt',
    'yritysedustajat': 'henkilöt',
    
    // organisaatiot
    'julkishallinto': 'organisaatiot',
    'järjestöt': 'organisaatiot',
    'kulttuuri': 'organisaatiot',
    'media': 'organisaatiot',
    'urheilu': 'organisaatiot',
    'uskonto': 'organisaatiot',
    'yritykset': 'organisaatiot',
    
    // tiedotteet
    'hankkeet': 'tiedotteet',
    'kaupungit': 'tiedotteet',
    'liikenne': 'tiedotteet',
    'terveydenhuolto': 'tiedotteet',
    'yhdyskuntapalvelut': 'tiedotteet',
    
    // tapahtumat
    'tapahtumat': 'tapahtumat',
    
    // laitokset
    'kirjastot': 'laitokset',
    'liikuntakeskukset': 'laitokset',
    'museot': 'laitokset',
    'oppilaitokset': 'laitokset',
    'uimahallit': 'laitokset',
    'virkistysalueet': 'laitokset',
    'kauppakeskukset': 'laitokset',
    
    // toimituksen sähköpostit
    'email': 'toimituksen sähköpostit',
};

export default function NewsCategoryIcon({ category, ...props }) {
    if (!category) {
        return null;
    }

    // First try to find a specific icon for this category
    if (SUBCATEGORY_ICONS[category]) {
        return React.cloneElement(SUBCATEGORY_ICONS[category], props);
    }

    // If no specific icon, try to find the main category and use its icon
    const mainCategory = CATEGORY_TO_MAIN_CATEGORY[category];
    if (mainCategory && MAIN_CATEGORY_ICONS[mainCategory]) {
        return React.cloneElement(MAIN_CATEGORY_ICONS[mainCategory], props);
    }

    // Fallback icon if no specific icon found
    return <SecurityIcon {...props} />;
}